import { useEffect, useId, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog } from '@mui/material';

import { BRAND } from '../../shared/theme/paletteDark';
import { feedUrls, type ScheduleLink } from '../account/accountApi';
import { useMe } from '../account/meContext';
import {
  CloseButton,
  Confirm,
  DialogHeading,
  DialogText,
  ErrorText,
  PrimaryLinkAction,
  SecondaryAction,
  SecondaryLink,
  SmallPrint,
  Stack,
  TextAction,
  UrlBox,
  dialogPaperSx,
} from './dialogParts';
import { useCopy } from './useCopy';

/**
 * Share My Schedule → "Sync with my personal calendar" (D12).
 *
 * A private calendar feed, subscribed to once: Google by a one-click link,
 * Apple through webcal://, Outlook through its add-from-web page, anything
 * else by pasting the URL. Opening the dialog makes the feed if there isn't
 * one yet — asking for it is the whole point of being here.
 */

const CALENDAR_NAME = 'My Climate Week';

/** Google's own "subscribe to this calendar" page. Sam to confirm in a real account. */
const googleUrl = (webcal: string) => `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`;

const outlookUrl = (host: 'outlook.live.com' | 'outlook.office.com', https: string) =>
  `https://${host}/calendar/0/addfromweb?url=${encodeURIComponent(https)}&name=${encodeURIComponent(CALENDAR_NAME)}`;

export default function CalendarSyncDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api } = useMe();
  const headingId = useId();
  const [feed, setFeed] = useState<ScheduleLink | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const [notice, setNotice] = useState('');
  const field = useRef<HTMLInputElement | null>(null);
  const { copy, copied, manual } = useCopy(() => field.current);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeed(null);
    setLoadFailed(false);
    setConfirm(false);
    setFailed('');
    setNotice('');
    api
      .createLink('feed')
      .then((link) => {
        if (!cancelled) setFeed(link);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, api, attempt]);

  const reset = async () => {
    setBusy(true);
    setFailed('');
    try {
      setFeed(await api.rotateLink('feed'));
      setConfirm(false);
      setNotice('New link ready. Calendars using the old one have stopped updating: add this one again.');
    } catch {
      setFailed('That didn’t work. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const urls = feed ? feedUrls(feed.token) : null;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={headingId} slotProps={{ paper: { sx: dialogPaperSx } }}>
      <CloseButton type="button" aria-label="Close" onClick={onClose}>
        ×
      </CloseButton>
      <DialogHeading id={headingId}>Sync with my personal calendar</DialogHeading>
      <DialogText>
        Subscribe once, and your calendar keeps up as you add and remove events here.
      </DialogText>

      {loadFailed && (
        <ErrorText role="alert">
          We couldn’t get your calendar link.{' '}
          <TextAction type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </TextAction>
        </ErrorText>
      )}
      {failed && <ErrorText role="alert">{failed}</ErrorText>}

      {!urls && !loadFailed && (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }} role="status" aria-label="Loading">
          <CircularProgress size={26} sx={{ color: BRAND.parchment }} />
        </Box>
      )}

      {urls && (
        <>
          <Stack>
            <PrimaryLinkAction href={googleUrl(urls.webcal)} target="_blank" rel="noopener noreferrer">
              Add to Google Calendar
            </PrimaryLinkAction>
            <SecondaryLink href={urls.webcal}>Add to Apple Calendar</SecondaryLink>
            <SecondaryLink href={outlookUrl('outlook.live.com', urls.https)} target="_blank" rel="noopener noreferrer">
              Add to Outlook
            </SecondaryLink>
          </Stack>
          <SmallPrint style={{ marginTop: 8 }}>
            Outlook through work or school?{' '}
            <a
              href={outlookUrl('outlook.office.com', urls.https)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: BRAND.parchment, fontWeight: 700 }}
            >
              Use this link instead
            </a>
            .
          </SmallPrint>

          <DialogText style={{ margin: '18px 0 8px' }}>
            Another calendar app? Copy the link and subscribe to it by URL.
          </DialogText>
          <UrlBox
            ref={field}
            readOnly
            value={urls.https}
            aria-label="Your calendar link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <SecondaryAction type="button" onClick={() => copy(urls.https)}>
            {copied ? 'Copied!' : 'Copy link'}
          </SecondaryAction>
          {manual && <SmallPrint role="status">Selected: press ⌘C, or Ctrl+C, to copy it.</SmallPrint>}

          <SmallPrint>
            Google Calendar only checks for changes every several hours, so something you change here can take a
            while to show up there.
          </SmallPrint>
          <SmallPrint style={{ marginTop: 6 }}>
            Keep this link to yourself: anyone who has it can see your schedule, personal events included.
          </SmallPrint>

          {confirm ? (
            <Confirm>
              Reset the link? Calendars using the current one stop updating, and you’ll need to add the new one
              again.
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
                <SecondaryAction
                  type="button"
                  style={{ width: 'auto', minHeight: 38 }}
                  disabled={busy}
                  aria-busy={busy}
                  onClick={reset}
                >
                  {busy ? 'Working…' : 'Reset link'}
                </SecondaryAction>
                <TextAction type="button" onClick={() => setConfirm(false)} disabled={busy}>
                  Cancel
                </TextAction>
              </Box>
            </Confirm>
          ) : (
            <Box sx={{ mt: 1 }}>
              <TextAction type="button" onClick={() => setConfirm(true)}>
                Reset link
              </TextAction>
            </Box>
          )}
        </>
      )}

      {notice && <SmallPrint role="status">{notice}</SmallPrint>}
    </Dialog>
  );
}
